import { useEffect } from 'react';
import { isCompletedSourceStatus } from '../../utils/collaborations/collaborationDisplayStatus';

const ACCEPTANCE_DETAILS_TTL_MS = 30000;
const detailsCache = new Map();
const inFlightDetails = new Map();
const inFlightBatches = new Map();

const getCacheKey = (sourceType, itemId) => `${sourceType}_${itemId}`;

export function resetAcceptanceDetailsCache() {
  detailsCache.clear();
  inFlightDetails.clear();
  inFlightBatches.clear();
}

async function fetchAcceptanceBySource(token, sourceType, itemId, signal) {
  const cacheKey = getCacheKey(sourceType, itemId);
  const cached = detailsCache.get(cacheKey);
  const now = Date.now();

  if (cached && now - cached.fetchedAt < ACCEPTANCE_DETAILS_TTL_MS) {
    return cached.data;
  }

  if (inFlightDetails.has(cacheKey)) {
    return inFlightDetails.get(cacheKey);
  }

  const request = (async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/acceptances/by-source/${sourceType}/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` }, signal },
      );
      if (res.ok) {
        const data = await res.json();
        detailsCache.set(cacheKey, { data, fetchedAt: Date.now() });
        return data;
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return null;
      }
    // Ignore details fetch failures and fall back to source row labels.
    } finally {
      inFlightDetails.delete(cacheKey);
    }
    return null;
  })();

  inFlightDetails.set(cacheKey, request);
  return request;
}

async function fetchAcceptanceDetailsBatch(token, items, signal) {
  const now = Date.now();
  const missingItems = [];
  const cachedDetails = {};

  items.forEach(({ sourceType, itemId }) => {
    const cacheKey = getCacheKey(sourceType, itemId);
    const cached = detailsCache.get(cacheKey);
    if (cached && now - cached.fetchedAt < ACCEPTANCE_DETAILS_TTL_MS) {
      cachedDetails[cacheKey] = cached.data;
    } else {
      missingItems.push({ sourceType, itemId });
    }
  });

  if (!missingItems.length) {
    return cachedDetails;
  }

  const batchKey = missingItems
    .map(({ sourceType, itemId }) => getCacheKey(sourceType, itemId))
    .sort()
    .join('|');

  if (inFlightBatches.has(batchKey)) {
    const batchDetails = await inFlightBatches.get(batchKey);
    return { ...cachedDetails, ...batchDetails };
  }

  const request = (async () => {
    const batchDetails = {};
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/acceptances/by-sources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: missingItems.map(({ sourceType, itemId }) => ({ sourceType, sourceId: itemId })),
        }),
        signal,
      });

      if (res.ok) {
        const { results = [] } = await res.json();
        results.forEach(({ sourceType, sourceId, acceptance }) => {
          const cacheKey = getCacheKey(sourceType, sourceId);
          detailsCache.set(cacheKey, { data: acceptance, fetchedAt: Date.now() });
          batchDetails[cacheKey] = acceptance;
        });
        return batchDetails;
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        return batchDetails;
      }
    }

    const fallbackRows = await Promise.all(missingItems.map(async ({ sourceType, itemId }) => {
      const data = await fetchAcceptanceBySource(token, sourceType, itemId, signal);
      return { sourceType, itemId, data };
    }));

    fallbackRows.forEach(({ sourceType, itemId, data }) => {
      if (data) {
        batchDetails[getCacheKey(sourceType, itemId)] = data;
      }
    });

    return batchDetails;
  })();

  inFlightBatches.set(batchKey, request);
  try {
    const batchDetails = await request;
    return { ...cachedDetails, ...batchDetails };
  } finally {
    inFlightBatches.delete(batchKey);
  }
}

/**
 * Loads acceptance detail records for active and finished invite/application sources.
 */
export default function useAcceptanceDetailsFetch({
  invites,
  applications,
  setAcceptanceDetails,
}) {
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return () => {};
    }
    const controller = new AbortController();
    let cancelled = false;

    const detailInvites = invites.filter(
      (i) => i.status === 'accepted' || i.status === 'active' || isCompletedSourceStatus(i.status),
    );
    const detailApplications = applications.filter(
      (a) => a.status === 'accepted' || a.status === 'approved' || isCompletedSourceStatus(a.status),
    );

    const fetchDetails = async () => {
      const details = await fetchAcceptanceDetailsBatch(token, [
        ...detailInvites.map((item) => ({ sourceType: 'invite', itemId: item.id })),
        ...detailApplications.map((item) => ({ sourceType: 'application', itemId: item.id })),
      ], controller.signal);
      const nextDetails = Object.entries(details).reduce((acc, [key, data]) => {
        acc[key] = { ...data };
        return acc;
      }, {});
      if (!cancelled) {
        setAcceptanceDetails((prev) => ({ ...prev, ...nextDetails }));
      }
    };

    if (detailInvites.length > 0 || detailApplications.length > 0) {
      fetchDetails();
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [applications, invites, setAcceptanceDetails]);
}
